import { defineStore } from 'pinia';
import { useModalsStore } from '~/stores/modals';
import { setCompareStore } from '@/composition/useLocalStore';
import { maxAge } from '@/common/const';
import type { compareItem } from '~/interfaces/app';

interface productsCount {
  [key: string]: number;
}

export type FavoriteState = {
  ids: number[];
  countInCategory: productsCount;
};

const cookieKey = 'compare';
const cookieCountKey = 'compare-count';

export const useCompareStore = defineStore('compare', () => {
  // STATES
  const ids = ref<number[]>([]);
  const countInCategory = ref({});
  const modalsStore = useModalsStore();

  function setCompareState(compare: { ids: number[]; count: productsCount }) {
    ids.value = [...compare.ids];
    countInCategory.value = compare.count;
  }

  function getCompareEnvironment() {
    const cookie = useCookie(cookieKey, {
      path: '/',
      maxAge,
    });

    const _ids = cookie.value
      ? cookie.value
          .toString()
          .split(',')
          .map((e) => +e)
      : [];

    const cookieCount = useCookie(cookieCountKey, {
      path: '/',
      maxAge,
    });

    const count = cookieCount.value
      ? Object.fromEntries(
          cookieCount.value
            .toString()
            .split(',')
            .map((e) => {
              const val = e.split('-');
              return [val[0], +val[1]];
            })
        )
      : {};

    if (_ids.length) setCompareState({ ids: _ids, count });
  }

  function toggleCompare({
    id,
    catId,
    deleteCard,
    hideNotification,
  }: compareItem) {
    const { domain } = hostData(window.location.hostname, window.location.port);
    let newIds: number[] = ids.value;
    const count: productsCount = { ...countInCategory.value };
    let deleted = !!deleteCard;

    if (newIds.includes(id)) {
      newIds = newIds.filter((fId) => fId !== id);
      if (--count[catId] < 1) delete count[catId];
      deleted = true;
    } else {
      deleted = false;
      if (newIds.length >= 300) {
        modalsStore.setData({
          name: 'limit',
          data: {
            type: 'compare',
          },
        });
        return;
      } else {
        newIds = [...newIds, id];
        count[catId] = count[catId] ? count[catId] + 1 : 1;
      }
    }
    const _cookieCount = useCookie(cookieCountKey, {
      path: '/',
      domain,
      maxAge,
    });
    _cookieCount.value = Object.entries(count)
      .map((el) => el.join('-'))
      .join(',');

    const _cookieKey = useCookie(cookieKey, {
      path: '/',
      domain,
      maxAge,
    });
    _cookieKey.value = newIds.join(',');

    setCompareState({ ids: newIds, count });
    setCompareStore({ ids: newIds, count });

    const isNotification = modalsStore.get.notification;
    const payload = {
      name: 'notification',
      data: {
        type: 'compare',
        categoryCount: count[catId],
        deleteCard: deleted,
        id,
        catId: Number(catId),
        date: Date.now(),
      },
    };
    if (!hideNotification) {
      isNotification
        ? modalsStore.update(payload)
        : modalsStore.setData(payload);
    }
  }

  return {
    ids,
    countInCategory,
    setCompareState,
    toggleCompare,
    getCompareEnvironment,
  };
});
